import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiLock, FiMail, FiMessageCircle, FiShield, FiSmartphone } from 'react-icons/fi';
import api from '../api';
import PublicLayout from '../components/PublicLayout';

function getAuthFeedback(error, fallback) {
  const data = error?.response?.data ?? {};
  const message =
    typeof data.message === 'string' && data.message.trim()
      ? data.message.trim()
      : fallback;

  return {
    allowImmediateResend: data.allowImmediateResend === true,
    message,
    registrationPending: data.registrationPending === true,
    recoveryAction:
      typeof data.recoveryAction === 'string' ? data.recoveryAction.trim() : '',
    status: error?.response?.status,
  };
}

function shouldContinueVerification(feedback) {
  return feedback.status === 403
    && (feedback.recoveryAction === 'complete_registration'
      || feedback.registrationPending);
}

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Both email and password are required.');
      return;
    }
    try {
      const res = await api.post('/users/login', { email, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/dashboard');
    } catch (err) {
      const feedback = getAuthFeedback(err, 'Login failed, please try again.');
      if (shouldContinueVerification(feedback)) {
        navigate('/register', {
          state: {
            infoMessage: feedback.message,
            initialForm: { email: email.trim() },
            initialStep: 'verify',
            pendingEmail: email.trim(),
            allowImmediateResend: feedback.allowImmediateResend,
          },
        });
        return;
      }
      setError(feedback.message);
    }
  };

  return (
    <PublicLayout>
      <div className="px-4 py-8 sm:px-6 sm:py-14">
        <div className="mx-auto grid min-h-[calc(100vh-13rem)] max-w-6xl gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <section className="rounded-[2.4rem] border border-[#ffcc00]/12 bg-[linear-gradient(180deg,#1c1b1b,#131313)] p-8 shadow-[0_24px_90px_rgba(0,0,0,0.25)] backdrop-blur-xl lg:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#ffe8a8]">Shimbirr account</p>
            <h1 className="mt-4 text-4xl font-black tracking-[-0.04em] text-white sm:text-5xl">
              Sign in to your verified Shimbirr identity.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-8 text-slate-300">
              Open the same account that powers your mobile inbox, trusted identity, connected-product approvals, and the wider Taban experience.
            </p>

            <div className="mt-8 grid gap-3">
              {[
                {
                  icon: FiMessageCircle,
                  title: 'Messages already warm',
                  body: 'Your latest chats, unread counters, and social state now preload faster after sign-in.',
                },
                {
                  icon: FiBell,
                  title: 'Unified notifications',
                  body: 'Replies, alerts, and app notifications stay connected to the same Shimbirr session.',
                },
                {
                  icon: FiShield,
                  title: 'Trusted account recovery',
                  body: 'Pending verification is routed back into OTP recovery instead of leaving the account stuck.',
                },
                {
                  icon: FiSmartphone,
                  title: 'Same identity everywhere',
                  body: 'Use one account across the Shimbirr mobile app, Taban approvals, and secure web access.',
                },
              ].map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-[1.6rem] border border-[#ffcc00]/10 bg-black/30 px-4 py-4 text-sm text-slate-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ffcc00]/14 text-[#ffe8a8]">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{title}</p>
                      <p className="mt-1 leading-6 text-slate-300">{body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[2.4rem] border border-[#ffcc00]/12 bg-[#1c1b1b]/96 p-8 shadow-[0_24px_90px_rgba(0,0,0,0.32)] backdrop-blur-xl lg:p-10">
            <h2 className="text-3xl font-black tracking-[-0.03em] text-white">Welcome back</h2>
            <p className="mt-2 text-sm text-slate-400">Enter your email and password to continue into Shimbirr.</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-300">Email</span>
                <div className="flex items-center rounded-full border border-[#ffcc00]/30 bg-black/30 px-5 py-3 text-white">
                  <FiMail className="mr-3 h-5 w-5 shrink-0 text-[#ffcc00]" />
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent text-white placeholder-slate-500 outline-none"
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-300">Password</span>
                <div className="flex items-center rounded-full border border-[#ffcc00]/30 bg-black/30 px-5 py-3 text-white">
                  <FiLock className="mr-3 h-5 w-5 shrink-0 text-[#ffcc00]" />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-transparent text-white placeholder-slate-500 outline-none"
                  />
                </div>
              </label>

              {error ? (
                <div className="rounded-[1.4rem] border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                className="w-full rounded-full bg-[#ffcc00] py-3 text-lg font-semibold text-[#131313] shadow-[0_18px_50px_rgba(255,204,0,0.24)] transition hover:-translate-y-0.5"
              >
                Sign in
              </button>

              <p className="text-center text-sm text-slate-300">
                Need a new account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="font-bold text-[#ffcc00] transition hover:text-[#ffe8a8]"
                >
                  Create one
                </button>
              </p>
            </form>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
